import type { ValidationConfig } from '../../../core/dataProcessor/types';

// 🆕 为新 ValidationEngine 导出的配置
export const wordCloudValidationConfig: ValidationConfig = {
  fields: [
    { field: 'x', type: 'string', required: true },
    { field: 'y', type: ['string', 'number'], required: true }
  ],
  businessRules: [
    {
      name: 'word-cloud-word-not-empty',
      validator: (data, layer) => {
        if (!Array.isArray(data)) {
          return false;
        }

        const { encoding } = layer;
        return data.every(item => {
          const word = item[encoding?.x as string];
          return word && typeof word === 'string' && word.trim().length > 0;
        });
      },
      message: 'Word cloud words cannot be empty',
      suggestion: 'Ensure all word fields contain non-empty string values',
      severity: 'error'
    },
    {
      name: 'word-cloud-frequency-positive',
      validator: (data, layer) => {
        if (!Array.isArray(data)) {
          return false;
        }

        const { encoding } = layer;
        return data.every(item => {
          const frequency = item[encoding?.y as string];

          // 支持字符串到数字的转换
          let numericFreq: number;
          if (typeof frequency === 'string') {
            numericFreq = parseFloat(frequency);
            // 检查转换是否成功
            if (Number.isNaN(numericFreq)) {
              return false;
            }
          } else if (typeof frequency === 'number') {
            numericFreq = frequency;
          } else {
            return false;
          }

          // 允许 >= 0 (包括0值)
          return numericFreq >= 0;
        });
      },
      message: 'Word cloud frequencies must be valid non-negative numbers',
      suggestion: 'Ensure all frequency values are valid numbers greater than or equal to 0',
      severity: 'error'
    },
    {
      name: 'word-cloud-sufficient-words',
      validator: data => {
        if (!Array.isArray(data)) {
          return false;
        }
        return data.length >= 5;
      },
      message: 'Word cloud works best with at least 5 words',
      suggestion: 'Provide at least 5 words for meaningful word cloud visualization',
      severity: 'warning'
    },
    {
      name: 'word-cloud-optimal-word-count',
      validator: data => {
        if (!Array.isArray(data)) {
          return false;
        }
        return data.length <= 200;
      },
      message: 'Word cloud works best with 200 or fewer words',
      suggestion: 'Consider limiting to 200 words for better readability and performance',
      severity: 'warning'
    },
    {
      name: 'word-cloud-word-length',
      validator: (data, layer) => {
        if (!Array.isArray(data)) {
          return false;
        }

        const { encoding } = layer;
        return data.every((item: Record<string, unknown>) => {
          const word = item[encoding?.x as string];
          return !word || typeof word !== 'string' || word.length <= 30;
        });
      },
      message: 'Word cloud words are very long',
      suggestion: 'Consider limiting word length to 30 characters for better display',
      severity: 'warning'
    },
    {
      name: 'word-cloud-frequency-distribution',
      validator: (data, layer) => {
        if (!Array.isArray(data)) {
          return false;
        }

        const { encoding } = layer;
        const frequencies = data
          .map((item: Record<string, unknown>) => item[encoding?.y as string])
          .filter((f: unknown) => typeof f === 'number');

        if (frequencies.length === 0) {
          return true;
        }

        const max = Math.max(...frequencies);
        const min = Math.min(...frequencies);

        // 检查频次分布是否合理（最大值不应该是最小值的1000倍以上）
        return max / min <= 1000;
      },
      message: 'Word cloud frequency distribution is highly skewed',
      suggestion: 'Consider normalizing frequencies to avoid extremely skewed distributions',
      severity: 'warning'
    },
    {
      name: 'word-cloud-unique-words',
      validator: (data, layer) => {
        if (!Array.isArray(data)) {
          return false;
        }

        const { encoding } = layer;
        const words = data.map((item: Record<string, unknown>) => item[encoding?.x as string]);
        const uniqueWords = new Set(words);
        return words.length === uniqueWords.size;
      },
      message: 'Word cloud contains duplicate words',
      suggestion: 'Ensure each word appears only once; aggregate frequencies for duplicate words',
      severity: 'warning'
    },
    {
      name: 'word-cloud-colors-sufficient',
      validator: (data, layer) => {
        const wordCloudLayer = layer as WordCloudLayerType;
        const { dvTextColors } = wordCloudLayer;

        if (!dvTextColors) {
          return true; // 使用默认颜色
        }

        return Array.isArray(dvTextColors) && dvTextColors.length >= 7;
      },
      message: 'Word cloud color array should contain at least 7 colors',
      suggestion: 'Provide at least 7 colors in dvTextColors array for proper color mapping',
      severity: 'warning'
    },
    {
      name: 'word-cloud-font-size-range-valid',
      validator: (data, layer) => {
        const wordCloudLayer = layer as WordCloudLayerType;
        const { dvFontSizeRange } = wordCloudLayer;

        if (!dvFontSizeRange) {
          return true;
        }

        // 检查是否为数组且长度为2
        if (!Array.isArray(dvFontSizeRange) || dvFontSizeRange.length !== 2) {
          return false;
        }

        // 检查类型是否为数字
        if (typeof dvFontSizeRange[0] !== 'number' || typeof dvFontSizeRange[1] !== 'number') {
          return false;
        }

        // 检查范围关系和边界值
        const minSize = dvFontSizeRange[0];
        const maxSize = dvFontSizeRange[1];

        return minSize < maxSize && minSize >= 8 && maxSize <= 100;
      },
      message: 'Word cloud font size range is invalid',
      suggestion: 'Set dvFontSizeRange as [min, max] where min < max and values are between 8-100',
      severity: 'warning'
    },
    {
      name: 'word-cloud-font-weight-range-valid',
      validator: (data, layer) => {
        const wordCloudLayer = layer as WordCloudLayerType;
        const { dvFontWeightRange } = wordCloudLayer;

        if (!dvFontWeightRange) {
          return true;
        }

        // 检查是否为数组且长度为2
        if (!Array.isArray(dvFontWeightRange) || dvFontWeightRange.length !== 2) {
          return false;
        }

        // 检查类型是否为数字
        if (typeof dvFontWeightRange[0] !== 'number' || typeof dvFontWeightRange[1] !== 'number') {
          return false;
        }

        // 检查范围关系和边界值
        const minWeight = dvFontWeightRange[0];
        const maxWeight = dvFontWeightRange[1];

        return minWeight < maxWeight && minWeight >= 100 && maxWeight <= 900;
      },
      message: 'Word cloud font weight range is invalid',
      suggestion:
        'Set dvFontWeightRange as [min, max] where min < max and values are between 100-900',
      severity: 'warning'
    }
  ]
};
